import { useEffect, useMemo, useState } from 'react';
import { helpSource, interpolateJargon, type HelpArticle } from '../lib/help';
import { useVertical } from '../context/VerticalContext';
import { useRole } from '../context/RoleContext';

/**
 * Help articles, ready to render: jargon substituted for the active vertical
 * and filtered down to what this user's role can actually act on.
 *
 * Filtering by permission is deliberate — a mechanic shown an article about
 * reading the tax pots would be reading about a screen they cannot open.
 */
export function useHelpArticles() {
    const { molded } = useVertical();
    const { can, loading: roleLoading } = useRole();
    const [raw, setRaw] = useState<HelpArticle[] | null>(null);

    useEffect(() => {
        let active = true;
        helpSource.listArticles().then((articles) => {
            if (active) setRaw(articles);
        });
        return () => { active = false; };
    }, []);

    const articles = useMemo(() => {
        if (!raw) return [];
        return raw
            .filter((a) => !a.permission || can(a.permission))
            .map((a) => ({
                ...a,
                title: interpolateJargon(a.title, molded.jargon),
                summary: interpolateJargon(a.summary, molded.jargon),
                body: interpolateJargon(a.body, molded.jargon),
            }));
    }, [raw, molded.jargon, can]);

    return {
        articles,
        // Role gates visibility, so hold the list until the role is known
        // rather than flashing articles that are about to disappear.
        loading: raw === null || roleLoading,
    };
}
